import { P2GBox, P2GButton, P2GDivider, P2GDrawer, P2GGrid, P2GText, P2GTextBox } from '@parcel2go/core-ui';
import { Trans, useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { Case, CreateCaseNoteResponse } from '~/lib/types/tracking-types';
import { TranslationNamespaces } from '~/lib/constants';
import { useState } from 'react';

const useCreateCaseStyles = makeStyles()((theme) => ({
  drawerRoot: {
    minWidth: '375px',
    [theme.breakpoints.down('xs')]: {
      minWidth: '100%'
    }
  }
}));

const CreateCommunication: React.FC<ITrackingCase> = (props) => {
  const { t } = useTranslation(TranslationNamespaces.TRACKING);
  const { classes } = useCreateCaseStyles();
  const [loading, setLoading] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [error, setError] = useState<string>();
  const [message, setMessage] = useState<string>('');

  async function send() {
    setLoading(true);
    const response = await props.onSendReply(props.caseDetails.id, message);
    if (response.success) {
      setOpen(false);
      setError(undefined);
    } else if (response.error) {
      setError(response.error.message);
    } else {
      setError('Failed');
    }
    setLoading(false);
  }

  return (
    <>
      <P2GButton variant="secondary" onClick={() => setOpen(true)}>
        <Trans t={t} i18nKey="RESULT_CASE_CREATE_OPEN" defaults="Add Reply" />
      </P2GButton>
      <P2GDrawer open={open} onClose={() => setOpen(false)} anchor="right">
        <P2GGrid container spacing={4} direction="column" classNames={classes.drawerRoot}>
          <P2GGrid item>
            <P2GText variant="h5">
              <Trans t={t} i18nKey="RESULT_CASE_CREATE_TITLE" defaults="Send us a reply" />
            </P2GText>
          </P2GGrid>
          <P2GGrid item>
            <P2GTextBox
              id="txtCaseMessage"
              label={t('RESULT_CASE_CREATE_TEXTBOX', {
                defaultValue: 'Reply'
              })}
              value={message}
              onChange={setMessage}
              multiline
              rows={15}
              rowsMax={20}
            />
          </P2GGrid>
          <P2GGrid item>
            <P2GButton variant="tertiary" processing={loading} onClick={send}>
              <Trans t={t} i18nKey="RESULT_CASE_CREATE_BUTTON" defaults="Send" />
            </P2GButton>
          </P2GGrid>
          {error && (
            <P2GGrid item>
              <P2GText colour="error">{error}</P2GText>
            </P2GGrid>
          )}
        </P2GGrid>
      </P2GDrawer>
    </>
  );
};

const CaseNotes: React.FC<ITrackingCase> = (props) => {
  return (
    <P2GGrid container direction="column" spacing={2}>
      {props.caseDetails.notes.map((n) => {
        return (
          <P2GGrid item>
            <P2GBox bordered p={2}>
              <P2GText component="p">{n.note}</P2GText>
              <P2GText component="p" variant="caption">
                {n.created}
              </P2GText>
            </P2GBox>
          </P2GGrid>
        );
      })}
    </P2GGrid>
  );
};

interface ITrackingCase {
  caseDetails: Case;
  onSendReply: (caseId: string, note: string) => Promise<CreateCaseNoteResponse>;
}

const TrackingCase: React.FC<ITrackingCase> = (props) => {
  const { t } = useTranslation(TranslationNamespaces.TRACKING);
  return (
    <P2GGrid container direction="column" spacing={2}>
      <P2GGrid item>
        <P2GText colour="error">
          <Trans
            t={t}
            i18nKey="RESULT_CASE_DETAIL_TITLE"
            defaults="A case {{id}} was created on {{created}} for your order"
            options={{ id: props.caseDetails.id, created: new Date(props.caseDetails.created).toDateString() }}
          />
        </P2GText>
      </P2GGrid>
      {props.caseDetails.notes.length > 0 && (
        <P2GGrid item>
          <P2GText variant="h6" component="h6">
            <Trans t={t} i18nKey="RESULT_CASE_DETAIL_NOTES" defaults="Case Notes" />
          </P2GText>
          <CaseNotes {...props} />
        </P2GGrid>
      )}
      <P2GGrid item>
        <CreateCommunication {...props} />
      </P2GGrid>
      <P2GGrid item>
        <P2GDivider />
      </P2GGrid>
    </P2GGrid>
  );
};

export default TrackingCase;
